import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space, 
  Tag, 
  Typography,
  message,
  Tooltip,
  Modal,
  Steps,
  QRCode,
  Result,
  Descriptions
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Transaction, QueryParams } from '../types';
import { formatAmount, formatDateTime, transactionTypeMap } from '../utils';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const TransactionList: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 模拟交易数据 - 包含所有流水类型
  const mockTransactions: Transaction[] = [
    {
      id: '1',
      plfm_ser_no: 'TXN202509210001',
      business_order_no: 'ORDER202509210001',
      txn_tp: '01',
      amount: '150000',
      counterpart: '杭州云数据服务有限公司',
      status: 'success',
      created_at: '2025-09-21T10:30:00Z',
      remark: '金融行业客户画像数据包 - 充值',
    },
    {
      id: '2',
      plfm_ser_no: 'TXN202509210002',
      business_order_no: 'ORDER202509210002',
      txn_tp: '21',
      amount: '50000',
      counterpart: '中国工商银行',
      status: 'success',
      created_at: '2025-09-21T09:15:00Z',
      remark: '提现到银行卡',
    },
    {
      id: '3',
      plfm_ser_no: 'TXN202509210003',
      business_order_no: 'ORDER202509210003',
      txn_tp: '03',
      amount: '80000',
      counterpart: '北京数据技术有限公司',
      status: 'success',
      created_at: '2025-09-21T08:45:00Z',
      remark: '房地产行业数据服务 - 付款',
    },
    {
      id: '4',
      plfm_ser_no: 'TXN202509200001',
      business_order_no: 'ORDER202509200001',
      txn_tp: '04',
      amount: '120000',
      counterpart: '深圳智能科技有限公司',
      status: 'success',
      created_at: '2025-09-20T16:20:00Z',
      remark: '制造业数据分析服务 - 收款',
    },
    {
      id: '5',
      plfm_ser_no: 'TXN202509200002',
      business_order_no: 'ORDER202509200002',
      txn_tp: '10',
      amount: '85000',
      counterpart: '广州商业数据公司',
      status: 'processing',
      created_at: '2025-09-20T14:10:00Z',
      remark: '零售行业消费数据包 - 退款',
    },
    {
      id: '6',
      plfm_ser_no: 'TXN202509190001',
      business_order_no: 'ORDER202509190001',
      txn_tp: '31',
      amount: '200000',
      counterpart: '上海银行',
      status: 'processing',
      created_at: '2025-09-19T11:30:00Z',
      remark: '分润付款',
    },
    {
      id: '7',
      plfm_ser_no: 'TXN202509190002',
      business_order_no: 'ORDER202509190002',
      txn_tp: '32',
      amount: '95000',
      counterpart: '南京数据服务中心',
      status: 'success',
      created_at: '2025-09-19T09:45:00Z',
      remark: '分润收款',
    },
    {
      id: '8',
      plfm_ser_no: 'TXN202509180001',
      business_order_no: 'ORDER202509180001',
      txn_tp: '33',
      amount: '30000',
      counterpart: '成都科技数据公司',
      status: 'failed',
      created_at: '2025-09-18T15:20:00Z',
      remark: '分润退款',
    },
    {
      id: '10',
      plfm_ser_no: 'TXN202509160001',
      business_order_no: 'ORDER202509160001',
      txn_tp: '01',
      amount: '100000',
      counterpart: '支付宝',
      status: 'success',
      created_at: '2025-09-16T14:30:00Z',
      remark: '账户充值',
    },
    {
      id: '11',
      plfm_ser_no: 'TXN202509150001',
      business_order_no: 'ORDER202509150001',
      txn_tp: '21',
      amount: '75000',
      counterpart: '招商银行',
      status: 'processing',
      created_at: '2025-09-15T11:20:00Z',
      remark: '提现申请',
    },
  ];

  // 交易状态标签颜色映射
  const statusColorMap: Record<string, string> = {
    success: 'green',
    failed: 'red',
    processing: 'blue',
  };

  // 交易状态文本映射
  const statusTextMap: Record<string, string> = {
    success: '成功',
    failed: '失败',
    processing: '处理中',
  };

  // 表格列定义
  const columns: ColumnsType<Transaction> = [
    {
      title: '交易流水号',
      dataIndex: 'plfm_ser_no',
      key: 'plfm_ser_no',
      width: 150,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace' }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: '业务订单号',
      dataIndex: 'business_order_no',
      key: 'business_order_no',
      width: 150,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace' }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: '交易类型',
      dataIndex: 'txn_tp',
      key: 'txn_tp',
      width: 120,
      render: (type: string) => (
        <Tag color="blue">{transactionTypeMap[type] || type}</Tag>
      ),
    },
    {
      title: '交易金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount: string) => (
        <span 
          style={{ 
            fontFamily: 'monospace', 
            fontWeight: 'bold',
            color: '#1890ff'
          }}
        >
          ¥{formatAmount(amount)}
        </span>
      ),
    },
    {
      title: '交易对手',
      dataIndex: 'counterpart',
      key: 'counterpart',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: '交易状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={statusColorMap[status]}>
          {statusTextMap[status]}
        </Tag>
      ),
    },
    {
      title: '交易时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => formatDateTime(time),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => {
        const isDownloading = downloadingReceipts.has(record.id);
        const isDownloaded = downloadedReceipts.has(record.id);
        
        return (
          <Space size="small">
            <Tooltip title="查看详情">
              <Button 
                type="text" 
                size="small" 
                icon={<EyeOutlined />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {record.status === 'success' && (
              isDownloaded ? (
                <Tooltip title="查看支付凭证">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<FileTextOutlined />}
                    onClick={() => handleViewReceipt(record)}
                  >
                    凭证
                  </Button>
                </Tooltip>
              ) : isDownloading ? (
                <Tooltip title="处理中">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<DownloadOutlined />}
                    loading
                    disabled
                  >
                    处理中
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="下载支付凭证">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadReceipt(record)}
                  >
                    凭证
                  </Button>
                </Tooltip>
              )
            )}
          </Space>
        );
      },
    },
  ];

  // 加载交易数据
  const loadTransactions = async (params?: QueryParams) => {
    setLoading(true);
    try {
      // 使用模拟数据
      await new Promise(resolve => setTimeout(resolve, 800));
      
      let filteredData = [...mockTransactions];
      
      // 模拟筛选逻辑
      if (params?.start_date && params?.end_date) {
        filteredData = filteredData.filter(item => {
          const itemDate = dayjs(item.created_at);
          return itemDate.isAfter(dayjs(params.start_date)) && 
                 itemDate.isBefore(dayjs(params.end_date));
        });
      }
      
      if (params?.status) {
        filteredData = filteredData.filter(item => item.status === params.status);
      }
      
      if (params?.type) {
        filteredData = filteredData.filter(item => item.txn_tp === params.type);
      }
      
      if (params?.plfm_ser_no) {
        filteredData = filteredData.filter(item => 
          item.plfm_ser_no.includes(params.plfm_ser_no!)
        );
      }
      
      if (params?.business_order_no) {
        filteredData = filteredData.filter(item => 
          item.business_order_no?.includes(params.business_order_no!)
        );
      }
      
      const startIndex = ((params?.page_num || 1) - 1) * (params?.page_size || 10);
      const endIndex = startIndex + (params?.page_size || 10);
      const pageData = filteredData.slice(startIndex, endIndex);
      
      setTransactions(pageData);
      setPagination({
        current: params?.page_num || 1,
        pageSize: params?.page_size || 10,
        total: filteredData.length,
      });
    } catch (error) {
      message.error('加载交易数据失败');
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // 搜索处理
  const handleSearch = (values: any) => {
    const params: QueryParams = {
      page_num: 1,
      page_size: pagination.pageSize,
    };
    
    if (values.plfm_ser_no) {
      params.plfm_ser_no = values.plfm_ser_no;
    }
    
    if (values.business_order_no) {
      params.business_order_no = values.business_order_no;
    }
    
    if (values.status) {
      params.status = values.status;
    }
    
    if (values.txn_tp) {
      params.type = values.txn_tp;
    }
    
    if (values.dateRange && values.dateRange.length === 2) {
      params.start_date = values.dateRange[0].format('YYYY-MM-DD');
      params.end_date = values.dateRange[1].format('YYYY-MM-DD');
    }
    
    loadTransactions(params);
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    loadTransactions({
      page_num: 1,
      page_size: pagination.pageSize,
    });
  };

  // 查看详情
  const handleViewDetail = (record: Transaction) => {
    setCurrentDetailRecord(record);
    setDetailModalVisible(true);
  };


  
  // 添加交易详情相关状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentDetailRecord, setCurrentDetailRecord] = useState<Transaction | null>(null);

  // 下载支付凭证状态管理
  const [downloadingReceipts, setDownloadingReceipts] = useState<Set<string>>(new Set(['1']));
  const [downloadedReceipts, setDownloadedReceipts] = useState<Set<string>>(new Set(['2']));

  // 下载支付凭证
  const handleDownloadReceipt = async (record: Transaction) => {
    // 添加到下载中集合
    setDownloadingReceipts(prev => new Set(prev).add(record.id));
    
    try {
      // 模拟下载过程
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // 下载完成，从下载中移除，添加到已下载集合
      setDownloadingReceipts(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
      setDownloadedReceipts(prev => new Set(prev).add(record.id));
      
      message.success('支付凭证下载完成！');
    } catch (error) {
      // 下载失败，从下载中移除
      setDownloadingReceipts(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
      message.error('下载失败，请重试');
    }
  };

  // 查看已下载凭证
  const handleViewReceipt = (record: Transaction) => {
    message.info('查看支付凭证');
    // 这里可以添加查看凭证的逻辑，比如打开新窗口或下载文件
  };



  // 表格分页变化处理
  const handleTableChange = (page: any, filters: any, sorter: any) => {
    const params: QueryParams = {
      page_num: page.current,
      page_size: page.pageSize,
    };
    
    // 保持当前的搜索条件
    const values = form.getFieldsValue();
    if (values.status) params.status = values.status;
    if (values.txn_tp) params.type = values.txn_tp;
    if (values.plfm_ser_no) params.plfm_ser_no = values.plfm_ser_no;
    if (values.business_order_no) params.business_order_no = values.business_order_no;
    if (values.dateRange && values.dateRange.length === 2) {
      params.start_date = values.dateRange[0].format('YYYY-MM-DD');
      params.end_date = values.dateRange[1].format('YYYY-MM-DD');
    }
    
    loadTransactions(params);
  };

  // 初始化加载数据
  useEffect(() => {
    loadTransactions();
  }, []);

  return (
    <div className="page-container">
      <Title level={2}>
        <FileTextOutlined style={{ marginRight: 12, color: '#1890ff' }} />
        交易流水查询
      </Title>

      {/* 搜索表单 */}
      <Card style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ gap: 16 }}
        >
          <Form.Item name="plfm_ser_no" label="交易流水号">
            <Input 
              placeholder="请输入交易流水号" 
              style={{ width: 200 }}
              allowClear
            />
          </Form.Item>
          
          <Form.Item name="business_order_no" label="业务订单号">
            <Input 
              placeholder="请输入业务订单号" 
              style={{ width: 200 }}
              allowClear
            />
          </Form.Item>
          
          <Form.Item name="txn_tp" label="交易类型">
            <Select 
              placeholder="选择交易类型" 
              style={{ width: 160 }}
              allowClear
            >
              <Option value="01">充值</Option>
              <Option value="21">提现</Option>
              <Option value="03">付款</Option>
              <Option value="04">收款</Option>
              <Option value="10">退款</Option>
              <Option value="31">分润付款</Option>
              <Option value="32">分润收款</Option>
              <Option value="33">分润退款</Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="status" label="交易状态">
            <Select 
              placeholder="选择交易状态" 
              style={{ width: 120 }}
              allowClear
            >
              <Option value="success">成功</Option>
              <Option value="failed">失败</Option>
              <Option value="processing">处理中</Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="dateRange" label="交易时间">
            <RangePicker 
              style={{ width: 250 }}
              format="YYYY-MM-DD"
              placeholder={['开始日期', '结束日期']}
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                icon={<SearchOutlined />} 
                htmlType="submit"
                loading={loading}
              >
                搜索
              </Button>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleReset}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 交易列表表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>



      {/* 交易详情模态框 */}
      <Modal
        title="交易详情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setCurrentDetailRecord(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setCurrentDetailRecord(null);
          }}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {currentDetailRecord && (
          <div>
            <Descriptions 
              column={2} 
              bordered
              size="middle"
              labelStyle={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}
            >
              <Descriptions.Item label="交易流水号">
                <span style={{ fontFamily: 'monospace' }}>{currentDetailRecord.plfm_ser_no}</span>
              </Descriptions.Item>
              
              <Descriptions.Item label="业务订单号">
                <span style={{ fontFamily: 'monospace' }}>{currentDetailRecord.business_order_no || '-'}</span>
              </Descriptions.Item>
              
              <Descriptions.Item label="交易类型">
                <Tag color="blue">{transactionTypeMap[currentDetailRecord.txn_tp] || currentDetailRecord.txn_tp}</Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="交易状态">
                <Tag color={{
                  success: 'green',
                  failed: 'red',
                  processing: 'blue'
                }[currentDetailRecord.status]}>
                  {{  
                    success: '成功',
                    failed: '失败',
                    processing: '处理中'
                  }[currentDetailRecord.status]}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="交易金额" span={2}>
                <span style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  color: '#1890ff',
                  fontFamily: 'monospace'
                }}>
                  ¥{formatAmount(currentDetailRecord.amount)}
                </span>
              </Descriptions.Item>
              
              <Descriptions.Item label="交易对手" span={2}>
                {currentDetailRecord.counterpart}
              </Descriptions.Item>
              
              <Descriptions.Item label="交易时间" span={2}>
                {formatDateTime(currentDetailRecord.created_at)}
              </Descriptions.Item>
              
              <Descriptions.Item label="备注说明" span={2}>
                {currentDetailRecord.remark || '-'}
              </Descriptions.Item>
            </Descriptions>
            
            {/* 根据交易状态显示下载凭证按钮 */}
            {currentDetailRecord.status === 'success' && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                {downloadedReceipts.has(currentDetailRecord.id) ? (
                  <Button 
                    type="primary" 
                    icon={<FileTextOutlined />}
                    onClick={() => handleViewReceipt(currentDetailRecord)}
                  >
                    查看支付凭证
                  </Button>
                ) : downloadingReceipts.has(currentDetailRecord.id) ? (
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                    loading
                    disabled
                  >
                    处理中
                  </Button>
                ) : (
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      setDetailModalVisible(false);
                      handleDownloadReceipt(currentDetailRecord);
                    }}
                  >
                    下载支付凭证
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TransactionList;